import React, { useState, useEffect } from 'react';
import { Project, JobOpening, InvestorLead, PartnershipLead, BilingualText, Language, Client } from '../types';
import Button from './ui/Button';
import Card from './ui/Card';
import { generatePrivateDetails, LOGO_BASE_URL } from '../constants';
import { Trash2 } from 'lucide-react';

// Helper icons for the UI
const ProjectsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor"><path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>;
const JobsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h2zm8-1V5a1 1 0 00-1-1H7a1 1 0 00-1 1v1h8z" clipRule="evenodd" /></svg>;
const InvestorsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" /></svg>;
const CategoriesIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>;
const PartnershipIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zM10 11a6 6 0 016 6H4a6 6 0 016-6z" /><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM2 10a8 8 0 1116 0 8 8 0 01-16 0z" clipRule="evenodd" /></svg>;

const PauseIcon = ({className = "w-5 h-5 mr-2"}) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>;
const PlayIcon = ({className = "w-5 h-5 mr-2"}) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>;


const formInputClass = "w-full bg-slate-700 text-white p-3 rounded-lg border border-slate-600 focus:ring-2 focus:ring-blue-500 focus:outline-none";

const ProjectForm = ({ project, onSave, onClose, categories, onAddCategory, t } : { project: Project | null, onSave: (data: any) => void, onClose: () => void, categories: string[], onAddCategory: (name: string) => Promise<boolean>, t: any }) => {
    const [formData, setFormData] = useState({
        name: '', city_es: '', city_en: '', area_es: '', area_en: '',
        publicDescription_es: '', publicDescription_en: '', iconUrl: '', partnershipAvailable: false,
    });
    const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isSavingCategory, setIsSavingCategory] = useState(false);

    useEffect(() => {
        if (project) {
            const iconElement = project.icon as React.ReactElement<any>;
            setFormData({
                name: project.name,
                city_es: typeof project.city === 'string' ? project.city : (project.city?.es || ''), 
                city_en: typeof project.city === 'string' ? project.city : (project.city?.en || ''),
                area_es: typeof project.area === 'string' ? project.area : (project.area?.es || ''), 
                area_en: typeof project.area === 'string' ? project.area : (project.area?.en || ''),
                publicDescription_es: typeof project.publicDescription === 'string' ? project.publicDescription : (project.publicDescription?.es || ''), 
                publicDescription_en: typeof project.publicDescription === 'string' ? project.publicDescription : (project.publicDescription?.en || ''),
                iconUrl: iconElement?.props?.src || project.iconUrl || '',
                partnershipAvailable: project.partnershipAvailable || false,
            });
        } else {
            setFormData({
                name: '', city_es: '', city_en: '', area_es: '', area_en: '',
                publicDescription_es: '', publicDescription_en: '', iconUrl: '', partnershipAvailable: false,
            });
        }
    }, [project]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        
        if (name === 'area_select') {
            if (value === 'NEW_CATEGORY') {
                setIsAddingNewCategory(true);
                return;
            }
            setFormData({ ...formData, area_es: value, area_en: value });
            return;
        }

        setFormData({ ...formData, [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value });
    }

    const handleSaveNewCategory = async () => {
        const trimmedName = newCategoryName.trim();
        if (trimmedName && !isSavingCategory) {
            console.log(`[ProjectForm] Attempting to save new category: "${trimmedName}"`);
            setIsSavingCategory(true);
            try {
                const success = await onAddCategory(trimmedName);
                if (success) {
                    setFormData({ ...formData, area_es: trimmedName, area_en: trimmedName });
                    setNewCategoryName('');
                    setIsAddingNewCategory(false);
                    console.log(`[ProjectForm] Category saved successfully: "${trimmedName}"`);
                } else {
                    console.error(`[ProjectForm] Failed to save category: "${trimmedName}"`);
                    alert('Error al añadir la categoría. Inténtalo de nuevo.');
                }
            } catch (err) {
                console.error(`[ProjectForm] Exception while saving category:`, err);
                alert('Ocurrió un error inesperado al guardar la categoría.');
            } finally {
                setIsSavingCategory(false);
            }
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    }

    const Label = ({ children }: { children: React.ReactNode }) => (
        <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">{children}</label>
    );

    return (
        <Card className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit}>
                <div className="flex justify-between items-center mb-8 border-b border-slate-700/50 pb-4">
                    <h2 className="text-3xl font-black text-white">{project ? t.projectFormEditTitle : t.projectFormAddTitle}</h2>
                    <div className="flex space-x-2">
                         <Button type="button" variant="ghost" onClick={onClose}>{t.cancel}</Button>
                         <Button type="submit" variant="primary">{t.save}</Button>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <div>
                            <Label>{t.projectFormName}</Label>
                            <input name="name" value={formData.name} onChange={handleChange} placeholder="Ej: Negolan 3.0" required className={formInputClass} />
                        </div>

                        <div className="space-y-4 p-4 bg-slate-800/40 rounded-xl border border-slate-700/50">
                            <div className="flex justify-between items-center mb-1">
                                <Label>Tipo de Proyecto (Categoría)</Label>
                                <button 
                                    type="button"
                                    onClick={() => setIsAddingNewCategory(!isAddingNewCategory)}
                                    className="text-[10px] font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors flex items-center"
                                >
                                    <span className="mr-1">+</span> {isAddingNewCategory ? 'Cancelar' : 'Nueva Categoría'}
                                </button>
                            </div>

                            {isAddingNewCategory ? (
                                <div className="flex space-x-2 animate-fade-in">
                                    <input 
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        placeholder="Nombre de la categoría..."
                                        className={`${formInputClass} flex-1`}
                                        autoFocus
                                        disabled={isSavingCategory}
                                    />
                                    <Button type="button" variant="primary" onClick={handleSaveNewCategory} className="px-4" disabled={isSavingCategory}>
                                        {isSavingCategory ? '...' : 'Añadir'}
                                    </Button>
                                </div>
                            ) : (
                                <select 
                                    name="area_select" 
                                    value={categories.includes(formData.area_es) ? formData.area_es : 'Otros'} 
                                    onChange={handleChange}
                                    className={`${formInputClass} cursor-pointer`}
                                >
                                    <option value="" disabled>Seleccione una categoría...</option>
                                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    <option value="NEW_CATEGORY">+ Añadir nueva...</option>
                                </select>
                            )}
                            
                            {formData.area_es === 'Otros' && !isAddingNewCategory && (
                                <div className="grid grid-cols-2 gap-3 mt-3 animate-fade-in">
                                    <div>
                                        <Label>Área personalizada (ES)</Label>
                                        <input name="area_es" value={formData.area_es === 'Otros' ? '' : formData.area_es} onChange={handleChange} placeholder="Personalizada" className={formInputClass} />
                                    </div>
                                    <div>
                                        <Label>Área personalizada (EN)</Label>
                                        <input name="area_en" value={formData.area_en === 'Otros' ? '' : formData.area_en} onChange={handleChange} placeholder="Custom Area" className={formInputClass} />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>{t.projectFormCityES}</Label>
                                <input name="city_es" value={formData.city_es} onChange={handleChange} placeholder="Andorra" required className={formInputClass} />
                            </div>
                            <div>
                                <Label>{t.projectFormCityEN}</Label>
                                <input name="city_en" value={formData.city_en} onChange={handleChange} placeholder="Andorra" required className={formInputClass} />
                            </div>
                        </div>

                        <div>
                            <Label>{t.projectFormIconURL}</Label>
                            <input name="iconUrl" value={formData.iconUrl} onChange={handleChange} placeholder="https://..." required className={formInputClass} />
                            {formData.iconUrl && (
                                <div className="mt-2 flex items-center space-x-3 p-2 bg-slate-900/50 rounded-lg border border-slate-700/30">
                                    <img 
                                        src={formData.iconUrl.startsWith('http') ? formData.iconUrl : `${LOGO_BASE_URL}${formData.iconUrl}`} 
                                        className="w-8 h-8 object-contain" 
                                        alt="Preview" 
                                        onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/32?text=Error'; }}
                                    />
                                    <span className="text-[10px] text-slate-500 truncate">
                                        {formData.iconUrl.startsWith('http') ? formData.iconUrl : `${LOGO_BASE_URL}${formData.iconUrl}`}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center p-3 bg-green-500/5 rounded-lg border border-green-500/20">
                            <input id="partnershipAvailable" name="partnershipAvailable" type="checkbox" checked={formData.partnershipAvailable} onChange={handleChange} className="h-5 w-5 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500" />
                            <label htmlFor="partnershipAvailable" className="ml-3 block text-sm font-semibold text-slate-200 cursor-pointer">{t.projectFormPartnership}</label>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-slate-800/40 p-4 rounded-xl border border-slate-700/50">
                            <Label>{t.projectFormDescES}</Label>
                            <textarea name="publicDescription_es" value={formData.publicDescription_es} onChange={handleChange} placeholder="Explicación en español..." required rows={6} className={formInputClass}></textarea>
                        </div>

                        <div className="bg-slate-800/40 p-4 rounded-xl border border-slate-700/50">
                            <Label>{t.projectFormDescEN}</Label>
                            <textarea name="publicDescription_en" value={formData.publicDescription_en} onChange={handleChange} placeholder="Explanation in English..." required rows={6} className={formInputClass}></textarea>
                        </div>
                    </div>
                </div>
            </form>
        </Card>
    );
};

const JobForm = ({ job, onSave, onClose, t } : { job: JobOpening | null, onSave: (data: any) => void, onClose: () => void, t: any }) => {
    const [formData, setFormData] = useState({
        title_es: '', title_en: '', description_es: '', description_en: '',
        location_es: '', location_en: '', type_es: '', type_en: ''
    });

    useEffect(() => {
        if (job) {
            setFormData({
                title_es: typeof job.title === 'string' ? job.title : (job.title?.es || ''), 
                title_en: typeof job.title === 'string' ? job.title : (job.title?.en || ''),
                description_es: typeof job.description === 'string' ? job.description : (job.description?.es || ''), 
                description_en: typeof job.description === 'string' ? job.description : (job.description?.en || ''),
                location_es: typeof job.location === 'string' ? job.location : (job.location?.es || ''), 
                location_en: typeof job.location === 'string' ? job.location : (job.location?.en || ''),
                type_es: typeof job.type === 'string' ? job.type : (job.type?.es || ''), 
                type_en: typeof job.type === 'string' ? job.type : (job.type?.en || ''),
            });
        } else {
             setFormData({
                title_es: '', title_en: '', description_es: '', description_en: '',
                location_es: '', location_en: '', type_es: '', type_en: ''
            });
        }
    }, [job]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    }
    
    return (
        <Card>
            <form onSubmit={handleSubmit}>
                <h2 className="text-2xl font-bold text-white mb-6">{job ? t.jobFormEditTitle : t.jobFormAddTitle}</h2>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <input name="title_es" value={formData.title_es} onChange={handleChange} placeholder={t.jobFormTitleES} required className={formInputClass} />
                      <input name="title_en" value={formData.title_en} onChange={handleChange} placeholder={t.jobFormTitleEN} required className={formInputClass} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input name="location_es" value={formData.location_es} onChange={handleChange} placeholder={t.jobFormLocationES} required className={formInputClass} />
                      <input name="location_en" value={formData.location_en} onChange={handleChange} placeholder={t.jobFormLocationEN} required className={formInputClass} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input name="type_es" value={formData.type_es} onChange={handleChange} placeholder={t.jobFormTypeES} required className={formInputClass} />
                      <input name="type_en" value={formData.type_en} onChange={handleChange} placeholder={t.jobFormTypeEN} required className={formInputClass} />
                    </div>
                    <div>
                      <textarea name="description_es" value={formData.description_es} onChange={handleChange} placeholder={t.jobFormDescES} required rows={4} className={formInputClass}></textarea>
                      <textarea name="description_en" value={formData.description_en} onChange={handleChange} placeholder={t.jobFormDescEN} required rows={4} className={`${formInputClass} mt-2`}></textarea>
                    </div>
                </div>
                <div className="flex justify-end space-x-4 mt-6">
                    <Button type="button" variant="ghost" onClick={onClose}>{t.cancel}</Button>
                    <Button type="submit" variant="primary">{t.save}</Button>
                </div>
            </form>
        </Card>
    );
};

const LeadForm = ({ lead, onSave, onClose, t } : { lead: InvestorLead | null, onSave: (data: any) => void, onClose: () => void, t: any }) => {
    const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '', status: 'active' });
    
    useEffect(() => {
        if (lead) {
            setFormData({
                name: lead.name,
                email: lead.email,
                company: lead.company || '',
                message: lead.message || '',
                status: lead.status || 'active'
            });
        }
    }, [lead]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.email) {
            alert('Nombre y Email son obligatorios.');
            return;
        }
        onSave(formData);
    }

    return (
         <Card>
            <form onSubmit={handleSubmit}>
                <h2 className="text-2xl font-bold text-white mb-6">{lead ? 'Editar Lead de Inversor' : t.leadFormTitle}</h2>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Nombre</label>
                            <input name="name" value={formData.name} onChange={handleChange} placeholder={t.leadFormName} required className={formInputClass} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Email</label>
                            <input name="email" value={formData.email} onChange={handleChange} type="email" placeholder={t.leadFormEmail} required className={formInputClass} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Empresa</label>
                        <input name="company" value={formData.company} onChange={handleChange} placeholder={t.leadFormCompany} className={formInputClass} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Estado</label>
                        <select name="status" value={formData.status} onChange={handleChange} className={formInputClass}>
                            <option value="active">Activo</option>
                            <option value="archived">Archivado</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Mensaje</label>
                        <textarea name="message" value={formData.message} onChange={handleChange} placeholder={t.leadFormMessage} rows={3} className={formInputClass}></textarea>
                    </div>
                </div>
                <div className="flex justify-end space-x-4 mt-6">
                    <Button type="button" variant="ghost" onClick={onClose}>{t.cancel}</Button>
                    <Button type="submit" variant="primary">{lead ? 'Guardar' : t.saveLeadButton}</Button>
                </div>
            </form>
        </Card>
    );
};

const ClientForm = ({ client, onSave, onClose, availableProjects } : { client: Client | null, onSave: (data: any) => void, onClose: () => void, availableProjects: Project[] }) => {
    const [formData, setFormData] = useState({
        name: '', email: '', password: '', project_ids: ''
    });

    useEffect(() => {
        if (client) {
            setFormData({
                name: client.name,
                email: client.email,
                password: '', // Password usually handled separately or empty if not changing
                project_ids: client.project_ids
            });
        }
    }, [client]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const toggleProject = (projectId: string) => {
        const currentIds = formData.project_ids.split(',').filter(id => id.length > 0);
        if (currentIds.includes(projectId)) {
            setFormData({ ...formData, project_ids: currentIds.filter(id => id !== projectId).join(',') });
        } else {
            setFormData({ ...formData, project_ids: [...currentIds, projectId].join(',') });
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    }

    return (
         <Card>
            <form onSubmit={handleSubmit}>
                <h2 className="text-2xl font-bold text-white mb-6">{client ? 'Editar Cliente' : 'Añadir Cliente'}</h2>
                <div className="space-y-4">
                    <input name="name" value={formData.name} onChange={handleChange} placeholder="Nombre del Cliente" required className={formInputClass} />
                    <input name="email" value={formData.email} onChange={handleChange} type="email" placeholder="Email de Acceso" required className={formInputClass} />
                    <input name="password" value={formData.password} onChange={handleChange} type="password" placeholder={client ? "Contraseña (dejar en blanco para no cambiar)" : "Contraseña"} required={!client} className={formInputClass} />
                    
                    <div className="pt-4">
                        <label className="block text-sm font-bold text-slate-300 mb-2">Acceso a Proyectos:</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {availableProjects.map(project => (
                                <div 
                                    key={project.id} 
                                    onClick={() => toggleProject(project.id)}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all ${formData.project_ids.split(',').includes(project.id) ? 'bg-blue-600/30 border-blue-500 text-white' : 'bg-slate-700/50 border-slate-600 text-slate-400'}`}
                                >
                                    <p className="text-xs font-bold">{project.name}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end space-x-4 mt-8">
                    <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
                    <Button type="submit" variant="primary">Guardar Cliente</Button>
                </div>
            </form>
        </Card>
    );
};

type AdminView = 'projects' | 'jobs' | 'investors' | 'partnerships' | 'clients' | 'categories';

interface AdminPanelProps {
    onClose: () => void;
    onLogout?: () => void;
    logoutText?: string;
    projects: Project[];
    setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
    jobs: JobOpening[];
    setJobs: React.Dispatch<React.SetStateAction<JobOpening[]>>;
    investorLeads: InvestorLead[];
    setInvestorLeads: React.Dispatch<React.SetStateAction<InvestorLead[]>>;
    partnershipLeads: PartnershipLead[];
    setPartnershipLeads: React.Dispatch<React.SetStateAction<PartnershipLead[]>>;
    language: Language;
    t: any;
}

const AdminPanel = ({ onClose, onLogout, logoutText, projects, setProjects, jobs, setJobs, investorLeads, setInvestorLeads, partnershipLeads, setPartnershipLeads, language, t }: AdminPanelProps) => {
    const [activeView, setActiveView] = useState<AdminView>('projects');
    
    const [isProjectFormOpen, setProjectFormOpen] = useState(false);
    const [editingProject, setEditingProject] = useState<Project | null>(null);

    const [isJobFormOpen, setJobFormOpen] = useState(false);
    const [editingJob, setEditingJob] = useState<JobOpening | null>(null);
    
    const [isLeadFormOpen, setLeadFormOpen] = useState(false);
    const [editingLead, setEditingLead] = useState<InvestorLead | null>(null);
    const [editingPartnership, setEditingPartnership] = useState<PartnershipLead | null>(null);

    const [clients, setClients] = useState<Client[]>([]);
    const [isClientFormOpen, setClientFormOpen] = useState(false);
    const [editingClient, setEditingClient] = useState<Client | null>(null);
    const [isLoadingClients, setIsLoadingClients] = useState(false);

    const [deletingCategory, setDeletingCategory] = useState<string | null>(null);
    const [categories, setCategories] = useState<string[]>([
        "Tecnología y Sociedad",
        "Ciberseguridad",
        "Domótica",
        "Smart City",
        "Cloud Computing",
        "Infraestructura",
        "Telecomunicaciones",
        "Incubadora",
        "LegalTech",
        "Fintech",
        "Servicios Profesionales",
        "Producción Audiovisual",
        "Agencia de Creadores",
        "IA y Big Data",
        "Blockchain y Web3",
        "Energía y Sostenibilidad",
        "Otros"
    ]);

    useEffect(() => {
        fetchCategories();
        if (activeView === 'clients') {
            fetchClients();
        }
    }, [activeView]);

    const fetchCategories = async () => {
        try {
            // Add a timestamp to avoid browser caching
            const response = await fetch(`/api/admin/categories?t=${Date.now()}`, {
                cache: 'no-store',
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
            });
            
            if (!response.ok) {
                console.error(`[AdminPanel] Failed to fetch categories: ${response.status}`);
                return;
            }

            const data = await response.json();
            if (data.success && Array.isArray(data.categories)) {
                setCategories(data.categories);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleAddCategory = async (name: string): Promise<boolean> => {
        if (!name) return false;
        console.log(`[AdminPanel] Adding category to backend: "${name}"`);
        try {
            const response = await fetch('/api/admin/categories', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name })
            });
            
            if (!response.ok) {
                let errorMsg = `HTTP ${response.status}`;
                try {
                    const errorDetails = await response.json();
                    errorMsg = errorDetails.error || errorMsg;
                } catch (e) {
                    // Not JSON, probably HTML error page
                }
                console.error(`[AdminPanel] Error adding category: ${errorMsg}`);
                return false;
            }

            const data = await response.json();
            if (data.success) {
                console.log(`[AdminPanel] Backend confirmed addition of: "${name}"`);
                // Optimistic update to make UI feel faster
                setCategories(prev => {
                    if (prev.includes(name)) return prev;
                    return [...prev, name].sort((a, b) => a.localeCompare(b));
                });
                // Sync with server in background
                fetchCategories();
                return true;
            }
        } catch (error) {
            console.error(`[AdminPanel] Network error adding category "${name}":`, error);
        }
        return false;
    };

    const fetchClients = async () => {
        setIsLoadingClients(true);
        try {
            const response = await fetch('/api/admin/clients');
            const data = await response.json();
            if (data.success) {
                setClients(data.clients);
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
        } finally {
            setIsLoadingClients(false);
        }
    };

    const handleDeleteCategory = async (name: string) => {
        console.log(`[AdminPanel] Requesting delete for category: "${name}"`);
        // Remove window.confirm as it might be blocked in some environments
        setDeletingCategory(name);
        try {
            const response = await fetch(`/api/admin/categories/${encodeURIComponent(name)}`, {
                method: 'DELETE'
            });
            console.log(`[AdminPanel] Delete response status: ${response.status}`);
            if (response.ok) {
                setCategories(prev => prev.filter(c => c !== name));
            } else {
                alert('No se pudo eliminar la categoría. Prueba de nuevo.');
            }
        } catch (error) {
            console.error('[AdminPanel] Error deleting category:', error);
            alert('Error de red al eliminar la categoría.');
        } finally {
            setDeletingCategory(null);
        }
    };

    const handleEditClient = (client: Client) => {
        setEditingClient(client);
        setClientFormOpen(true);
    };

    const handleAddClient = () => {
        setEditingClient(null);
        setClientFormOpen(true);
    };

    const handleDeleteClient = async (id: number) => {
        if (window.confirm('¿Seguro que quieres eliminar este cliente?')) {
            try {
                const response = await fetch(`/api/admin/clients/${id}`, { method: 'DELETE' });
                const data = await response.json();
                if (data.success) {
                    fetchClients();
                }
            } catch (error) {
                console.error('Error deleting client:', error);
            }
        }
    };

    const handleSaveClient = async (data: any) => {
        try {
            const response = await fetch('/api/admin/clients', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingClient ? { ...data, id: editingClient.id } : data),
            });
            const result = await response.json();
            if (result.success) {
                fetchClients();
                setClientFormOpen(false);
                setEditingClient(null);
            }
        } catch (error) {
            console.error('Error saving client:', error);
        }
    };

    const handleEditProject = (project: Project) => {
        setEditingProject(project);
        setProjectFormOpen(true);
    };

    const handleAddProject = () => {
        setEditingProject(null);
        setProjectFormOpen(true);
    };

    const handleDeleteProject = async (id: string) => {
        if (window.confirm(t.confirmDeleteProject)) {
            try {
                const response = await fetch(`/api/admin/projects/${id}`, { method: 'DELETE' });
                if (response.ok) {
                    setProjects(projects.filter(p => p.id !== id));
                }
            } catch (error) {
                console.error("Error deleting project:", error);
            }
        }
    };
    
    const handleToggleProjectPause = async (id: string) => {
        const project = projects.find(p => p.id === id);
        if (!project) return;
        
        try {
            const updatedProject = { ...project, is_paused: !project.isPaused };
            const response = await fetch('/api/admin/projects', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedProject)
            });
            if (response.ok) {
                setProjects(projects.map(p => p.id === id ? { ...p, isPaused: !p.isPaused } : p));
            }
        } catch (error) {
            console.error("Error toggling project pause:", error);
        }
    };

    const handleSaveProject = async (data: any) => {
        const newId = editingProject ? editingProject.id : `project-${Date.now()}`;
        const projectData = {
          id: newId,
          name: data.name,
          city: { es: data.city_es, en: data.city_en },
          area: { es: data.area_es, en: data.area_en },
          public_description: { es: data.publicDescription_es, en: data.publicDescription_en },
          icon_url: data.iconUrl,
          partnership_available: data.partnershipAvailable,
          private_details: editingProject?.privateDetails || generatePrivateDetails(newId),
          is_paused: editingProject?.isPaused || false
        };

        try {
            const response = await fetch('/api/admin/projects', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(projectData)
            });
            
            if (response.ok) {
                // Reconstruct the object for local state (map public_description -> publicDescription)
                const localizedProject: Project = {
                    ...projectData,
                    publicDescription: projectData.public_description,
                    partnershipAvailable: projectData.partnership_available,
                    privateDetails: projectData.private_details,
                    isPaused: projectData.is_paused,
                    icon: <img src={projectData.icon_url} alt={`Logo de ${projectData.name}`} className="w-8 h-8 object-contain" />
                };

                if (editingProject) {
                    setProjects(projects.map(p => p.id === editingProject.id ? localizedProject : p));
                } else {
                    setProjects(prev => [...prev, localizedProject]);
                }
                setProjectFormOpen(false);
                setEditingProject(null);
            }
        } catch (error) {
            console.error("Error saving project:", error);
        }
    };

    const handleEditJob = (job: JobOpening) => {
        setEditingJob(job);
        setJobFormOpen(true);
    };

    const handleAddJob = () => {
        setEditingJob(null);
        setJobFormOpen(true);
    };

    const handleDeleteJob = async (id: string) => {
        if (window.confirm(t.confirmDeleteJob)) {
            try {
                const response = await fetch(`/api/admin/jobs/${id}`, { method: 'DELETE' });
                if (response.ok) {
                    setJobs(jobs.filter(j => j.id !== id));
                }
            } catch (error) {
                console.error("Error deleting job:", error);
            }
        }
    };
    
    const handleToggleJobPause = async (id: string) => {
        const job = jobs.find(j => j.id === id);
        if (!job) return;

        try {
            const updatedJob = { ...job, is_paused: !job.isPaused };
            const response = await fetch('/api/admin/jobs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedJob)
            });
            if (response.ok) {
                setJobs(jobs.map(j => j.id === id ? { ...j, isPaused: !j.isPaused } : j));
            }
        } catch (error) {
            console.error("Error toggling job pause:", error);
        }
    };

    const handleSaveJob = async (data: any) => {
        const newId = editingJob ? editingJob.id : `job-${Date.now()}`;
        const jobData = {
            id: newId,
            title: { es: data.title_es, en: data.title_en },
            description: { es: data.description_es, en: data.description_en },
            location: { es: data.location_es, en: data.location_en },
            type: { es: data.type_es, en: data.type_en },
            is_paused: editingJob?.isPaused || false
        };

        try {
            const response = await fetch('/api/admin/jobs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(jobData)
            });

            if (response.ok) {
                const localizedJob: JobOpening = {
                    ...jobData,
                    isPaused: jobData.is_paused
                };

                if (editingJob) {
                    setJobs(jobs.map(j => j.id === editingJob.id ? localizedJob : j));
                } else {
                    setJobs(prev => [...prev, localizedJob]);
                }
                setJobFormOpen(false);
                setEditingJob(null);
            }
        } catch (error) {
            console.error("Error saving job:", error);
        }
    }
    
    const handleSaveLead = async (data: any) => {
        const leadData = {
            ...data,
            id: editingLead?.id
        };

        try {
            const response = await fetch('/api/admin/leads/investors', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(leadData)
            });

            if (response.ok) {
                // Refresh our local list
                const res = await fetch('/api/admin/leads/investors');
                const result = await res.json();
                if (result.success) {
                    setInvestorLeads(result.leads.map((l: any) => ({ ...l, date: new Date(l.created_at) })));
                }
                setLeadFormOpen(false);
                setEditingLead(null);
            }
        } catch (error) {
            console.error("Error saving lead:", error);
        }
    };

    const handleDeleteInvestorLead = async (id: string) => {
        if (window.confirm('¿Eliminar este lead de inversor?')) {
            try {
                const response = await fetch(`/api/admin/leads/investors/${id}`, { method: 'DELETE' });
                if (response.ok) {
                    setInvestorLeads(prev => prev.filter(l => l.id !== id));
                }
            } catch (error) {
                console.error("Error deleting investor lead:", error);
            }
        }
    };

    const handleToggleInvestorArchive = async (lead: InvestorLead) => {
        const newStatus = lead.status === 'archived' ? 'active' : 'archived';
        try {
            const response = await fetch('/api/admin/leads/investors', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...lead, status: newStatus })
            });
            if (response.ok) {
                setInvestorLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
            }
        } catch (err) {
            console.error("Error archiving lead:", err);
        }
    };

    const handleDeletePartnershipLead = async (id: string) => {
        if (window.confirm('¿Eliminar esta propuesta de partnership?')) {
            try {
                const response = await fetch(`/api/admin/leads/partnerships/${id}`, { method: 'DELETE' });
                if (response.ok) {
                    setPartnershipLeads(prev => prev.filter(l => l.id !== id));
                }
            } catch (error) {
                console.error("Error deleting partnership lead:", error);
            }
        }
    };

    const handleTogglePartnershipArchive = async (lead: PartnershipLead) => {
        const newStatus = lead.status === 'archived' ? 'active' : 'archived';
        try {
            // Map camelCase to snake_case for the backend
            const dbData = {
                id: lead.id,
                contact_person: lead.contactPerson,
                company_name: lead.companyName,
                email: lead.email,
                phone: lead.phone,
                website: lead.website,
                country: lead.country,
                project_name: lead.projectName,
                collaboration_type: lead.collaborationType,
                estimated_budget: lead.estimatedBudget,
                company_description: lead.companyDescription,
                detailed_proposal: lead.detailedProposal,
                nda_accepted: lead.ndaAccepted,
                status: newStatus
            };

            const response = await fetch('/api/admin/leads/partnerships', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dbData)
            });
            if (response.ok) {
                setPartnershipLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status: newStatus } : l));
            }
        } catch (err) {
            console.error("Error archiving lead:", err);
        }
    };

    const renderContent = () => {
        switch(activeView) {
            case 'projects':
                return isProjectFormOpen ? (
                    <ProjectForm 
                        project={editingProject} 
                        categories={categories}
                        onAddCategory={handleAddCategory}
                        onSave={handleSaveProject} 
                        onClose={() => setProjectFormOpen(false)} 
                        t={t}
                    />
                ) : (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-white">{t.manageProjectsTitle} ({projects.length})</h2>
                            <Button variant="secondary" onClick={handleAddProject}>{t.addProjectButton}</Button>
                        </div>
                        <div className="space-y-4">
                            {projects.map(p => (
                                <Card key={p.id} className={`flex flex-col sm:flex-row justify-between sm:items-center gap-4 transition-opacity ${p.isPaused ? 'opacity-50' : ''}`}>
                                    <div>
                                        <h3 className="font-bold text-lg text-white">{p.name}</h3>
                                        <p className="text-sm text-slate-400">{typeof p.area === 'string' ? p.area : (p.area?.[language] || p.area?.['es'] || '')}</p>
                                    </div>
                                    <div className="flex items-center space-x-2 flex-wrap gap-y-2 mt-2 sm:mt-0">
                                        <Button variant={p.isPaused ? "secondary" : "ghost"} className="text-sm px-3 py-1 flex items-center" onClick={() => handleToggleProjectPause(p.id)}>
                                          {p.isPaused ? <PlayIcon /> : <PauseIcon />}
                                          <span className="ml-1">{p.isPaused ? t.resume : t.pause}</span>
                                        </Button>
                                        <Button variant="ghost" className="text-sm px-3 py-1" onClick={() => handleEditProject(p)}>{t.edit}</Button>
                                        <Button variant="accent" className="text-sm px-3 py-1" onClick={() => handleDeleteProject(p.id)}>{t.delete}</Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                );
            case 'jobs':
                 return isJobFormOpen ? <JobForm job={editingJob} onSave={handleSaveJob} onClose={() => setJobFormOpen(false)} t={t} /> : (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                             <h2 className="text-2xl font-bold text-white">{t.manageJobsTitle} ({jobs.length})</h2>
                             <Button variant="secondary" onClick={handleAddJob}>{t.addJobButton}</Button>
                        </div>
                        <div className="space-y-4">
                            {jobs.map(j => (
                                <Card key={j.id} className={`flex flex-col sm:flex-row justify-between sm:items-center gap-4 transition-opacity ${j.isPaused ? 'opacity-50' : ''}`}>
                                    <div>
                                        <h3 className="font-bold text-lg text-white">{typeof j.title === 'string' ? j.title : (j.title?.[language] || j.title?.['es'] || '')}</h3>
                                        <p className="text-sm text-slate-400">{typeof j.location === 'string' ? j.location : (j.location?.[language] || j.location?.['es'] || '')}</p>
                                    </div>
                                    <div className="flex items-center space-x-2 flex-wrap gap-y-2 mt-2 sm:mt-0">
                                        <Button variant={j.isPaused ? "secondary" : "ghost"} className="text-sm px-3 py-1 flex items-center" onClick={() => handleToggleJobPause(j.id)}>
                                          {j.isPaused ? <PlayIcon /> : <PauseIcon />}
                                          <span className="ml-1">{j.isPaused ? t.resume : t.pause}</span>
                                        </Button>
                                        <Button variant="ghost" className="text-sm px-3 py-1" onClick={() => handleEditJob(j)}>{t.edit}</Button>
                                        <Button variant="accent" className="text-sm px-3 py-1" onClick={() => handleDeleteJob(j.id)}>{t.delete}</Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                );
            case 'investors':
                return isLeadFormOpen ? <LeadForm lead={editingLead} onSave={handleSaveLead} onClose={() => { setLeadFormOpen(false); setEditingLead(null); }} t={t} /> : (
                    <div>
                         <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-white">{t.investorLeadsTitle} ({investorLeads.length})</h2>
                            <Button variant="secondary" onClick={() => { setEditingLead(null); setLeadFormOpen(true); }}>{t.addLeadButton}</Button>
                        </div>
                        <div className="space-y-4">
                            {investorLeads.length > 0 ? [...investorLeads].sort((a,b) => b.date.getTime() - a.date.getTime()).map(lead => (
                                <Card key={lead.id} className={`${lead.status === 'archived' ? 'opacity-50 grayscale line-through' : ''} transition-all duration-300`}>
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-bold text-lg text-white">
                                                {lead.name} 
                                                <span className="text-base font-normal text-slate-300 ml-2">&lt;{lead.email}&gt;</span>
                                                {lead.status === 'archived' && <span className="ml-2 text-[10px] bg-slate-700 px-2 py-0.5 rounded text-slate-400 uppercase no-underline inline-block">Archivado</span>}
                                            </h3>
                                            <p className="text-sm text-blue-400 font-semibold">{lead.company || "Sin empresa"}</p>
                                        </div>
                                        <div className="text-right no-underline">
                                            <p className="text-xs text-slate-500">{lead.date.toLocaleString(language)}</p>
                                            <div className="flex space-x-1 mt-2">
                                                <Button variant="ghost" className="text-[10px] px-2 py-1" onClick={() => { setEditingLead(lead); setLeadFormOpen(true); }}>{t.edit}</Button>
                                                <Button variant="secondary" className="text-[10px] px-2 py-1" onClick={() => handleToggleInvestorArchive(lead)}>
                                                    {lead.status === 'archived' ? 'Activar' : 'Archivar'}
                                                </Button>
                                                <Button variant="accent" className="text-[10px] px-2 py-1" onClick={() => handleDeleteInvestorLead(lead.id)}>{t.delete}</Button>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-4 text-slate-300 bg-slate-900/50 p-3 rounded-md">{lead.message || 'Sin mensaje adicional.'}</p>
                                </Card>
                            )) : <p className="text-slate-400">{t.noInvestorLeads}</p>}
                        </div>
                    </div>
                );
            case 'partnerships':
                return (
                     <div>
                         <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-white">{t.partnershipLeadsTitle} ({partnershipLeads.length})</h2>
                        </div>
                        <div className="space-y-4">
                            {partnershipLeads.length > 0 ? [...partnershipLeads].sort((a,b) => b.date.getTime() - a.date.getTime()).map(lead => (
                                <Card key={lead.id} className={`${lead.status === 'archived' ? 'opacity-50 grayscale line-through' : ''} transition-all duration-300`}>
                                    <div className="flex justify-between items-start flex-wrap gap-2">
                                        <div>
                                            <h3 className="font-bold text-lg text-white">
                                                {lead.contactPerson} - <span className="text-base font-normal text-slate-300">{lead.companyName}</span>
                                                {lead.status === 'archived' && <span className="ml-2 text-[10px] bg-slate-700 px-2 py-0.5 rounded text-slate-400 uppercase no-underline inline-block">Archivado</span>}
                                            </h3>
                                            <p className="text-sm text-slate-400 no-underline">
                                                <a href={`mailto:${lead.email}`} className="text-blue-400 hover:underline">{lead.email}</a>
                                                {lead.website && <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline ml-2">({lead.website})</a>}
                                            </p>
                                            <p className="text-sm text-slate-400 no-underline">{lead.phone} ({lead.country})</p>
                                        </div>
                                        <div className="text-right no-underline">
                                            <p className="text-xs text-slate-500 flex-shrink-0">{lead.date.toLocaleString(language)}</p>
                                            <div className="flex space-x-1 mt-2">
                                                <Button variant="secondary" className="text-[10px] px-2 py-1" onClick={() => handleTogglePartnershipArchive(lead)}>
                                                    {lead.status === 'archived' ? 'Activar' : 'Archivar'}
                                                </Button>
                                                <Button variant="accent" className="text-[10px] px-2 py-1" onClick={() => handleDeletePartnershipLead(lead.id)}>{t.delete}</Button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                                         <p className="text-sm"><span className="font-semibold text-slate-200">{t.interestProject}:</span> <span className="text-green-400 font-bold">{lead.projectName}</span></p>
                                         <p className="text-sm"><span className="font-semibold text-slate-200">{t.collaborationType}:</span> {lead.collaborationType}</p>
                                         {lead.estimatedBudget && <p className="text-sm"><span className="font-semibold text-slate-200">{t.budget}:</span> {lead.estimatedBudget}</p>}
                                    </div>

                                    <div className="mt-4 space-y-3">
                                        <div>
                                            <h4 className="font-semibold text-slate-200">{t.companyDescription}</h4>
                                            <p className="text-slate-300 bg-slate-900/50 p-3 rounded-md mt-1 text-sm">{lead.companyDescription || "No proporcionada."}</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-slate-200">{t.detailedProposal}</h4>
                                            <p className="text-slate-300 bg-slate-900/50 p-3 rounded-md mt-1 text-sm">{lead.detailedProposal}</p>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-slate-700/50 text-right text-xs text-slate-500 no-underline">
                                        {t.ndaAccepted}: {lead.ndaAccepted ? t.yes : t.no}
                                    </div>
                                </Card>
                            )) : <p className="text-slate-400">{t.noPartnershipLeads}</p>}
                        </div>
                    </div>
                );
            case 'clients':
                return isClientFormOpen ? <ClientForm client={editingClient} onSave={handleSaveClient} onClose={() => setClientFormOpen(false)} availableProjects={projects} /> : (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-white">Gestionar Clientes (D1)</h2>
                            <Button variant="secondary" onClick={handleAddClient}>Añadir Cliente</Button>
                        </div>
                        {isLoadingClients ? (
                           <div className="py-20 text-center text-slate-400">Cargando clientes...</div>
                        ) : (
                            <div className="space-y-4">
                                {clients.length > 0 ? clients.map(client => (
                                    <Card key={client.id} className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                                        <div>
                                            <h3 className="font-bold text-lg text-white">{client.name}</h3>
                                            <p className="text-sm text-slate-400">{client.email}</p>
                                            <div className="flex gap-2 mt-1">
                                                {client.project_ids.split(',').filter((id: string) => id.length > 0).map((id: string) => {
                                                    const p = projects.find((proj: Project) => proj.id === id);
                                                    return p ? <span key={id} className="text-[10px] bg-slate-700 px-2 py-0.5 rounded text-slate-300">{p.name}</span> : null;
                                                })}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Button variant="ghost" className="text-sm px-3 py-1" onClick={() => handleEditClient(client)}>{t.edit}</Button>
                                            <Button variant="accent" className="text-sm px-3 py-1" onClick={() => handleDeleteClient(client.id)}>{t.delete}</Button>
                                        </div>
                                    </Card>
                                )) : <p className="text-slate-400">No hay clientes registrados en D1.</p>}
                            </div>
                        )}
                    </div>
                );
            case 'categories':
                return (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-white">Gestionar Categorías de Proyecto ({categories.length})</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {categories.map(cat => (
                                <Card key={cat} className={`flex items-center justify-between p-3 transition-all ${deletingCategory === cat ? 'opacity-50' : 'bg-slate-800/50 hover:bg-slate-800'}`}>
                                    <span className="text-sm font-medium text-slate-200">{cat}</span>
                                    <button 
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDeleteCategory(cat);
                                        }}
                                        disabled={deletingCategory !== null}
                                        className={`p-2 rounded-lg transition-all z-10 cursor-pointer ${deletingCategory === cat ? 'text-slate-500' : 'text-slate-400 hover:text-red-400 hover:bg-red-400/10'}`}
                                        title="Eliminar categoría"
                                    >
                                        {deletingCategory === cat ? (
                                            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <Trash2 size={16} />
                                        )}
                                    </button>
                                </Card>
                            ))}
                        </div>
                    </div>
                );
            default: return null;
        }
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                <h1 className="text-3xl sm:text-4xl font-black text-white">{t.title}</h1>
                <div className="flex flex-wrap items-center gap-3">
                    {onLogout && logoutText && (
                        <Button onClick={onLogout} variant="accent" className="px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base">
                            {logoutText}
                        </Button>
                    )}
                    <Button onClick={onClose} variant="secondary" className="px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base">
                        {t.closeButton}
                    </Button>
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                <aside className="md:col-span-1">
                    <Card className="p-4">
                        <nav className="flex flex-col space-y-2">
                            <button onClick={() => setActiveView('projects')} className={`flex items-center w-full text-left p-3 rounded-lg transition-colors ${activeView === 'projects' ? 'bg-blue-500/20 text-blue-300' : 'hover:bg-slate-700/50 text-slate-300'}`}>
                                <ProjectsIcon /> {t.navProjects}
                            </button>
                            <button onClick={() => setActiveView('jobs')} className={`flex items-center w-full text-left p-3 rounded-lg transition-colors ${activeView === 'jobs' ? 'bg-purple-500/20 text-purple-300' : 'hover:bg-slate-700/50 text-slate-300'}`}>
                                <JobsIcon /> {t.navJobs}
                            </button>
                            <button onClick={() => setActiveView('investors')} className={`flex items-center w-full text-left p-3 rounded-lg transition-colors ${activeView === 'investors' ? 'bg-rose-500/20 text-rose-300' : 'hover:bg-slate-700/50 text-slate-300'}`}>
                                <InvestorsIcon /> {t.navInvestors}
                            </button>
                             <button onClick={() => setActiveView('partnerships')} className={`flex items-center w-full text-left p-3 rounded-lg transition-colors ${activeView === 'partnerships' ? 'bg-green-500/20 text-green-300' : 'hover:bg-slate-700/50 text-slate-300'}`}>
                                <PartnershipIcon /> {t.navPartnerships}
                            </button>
                            <button onClick={() => setActiveView('clients')} className={`flex items-center w-full text-left p-3 rounded-lg transition-colors ${activeView === 'clients' ? 'bg-yellow-500/20 text-yellow-300' : 'hover:bg-slate-700/50 text-slate-300'}`}>
                                <InvestorsIcon /> Clientes (SQL)
                            </button>
                            <button onClick={() => setActiveView('categories')} className={`flex items-center w-full text-left p-3 rounded-lg transition-colors ${activeView === 'categories' ? 'bg-cyan-500/20 text-cyan-300' : 'hover:bg-slate-700/50 text-slate-300'}`}>
                                <CategoriesIcon /> Categorías
                            </button>
                        </nav>
                    </Card>
                </aside>

                <main className="md:col-span-3">
                    <Card className="min-h-[60vh] p-8">
                        {renderContent()}
                    </Card>
                </main>
            </div>
        </div>
    );
};

export default AdminPanel;